import logger from "logging/logger";
import {
  FacebookAuthProvider,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  signInWithPopup,
  UserCredential,
  signInWithRedirect as authSignInWithRedirect,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import { firebaseAuth } from "services/firebaseService/firebaseConfig";
import { FirebaseError } from "firebase/app";
import { toggleSSOEventLinkSource } from "./ssoUtils";
import { PreferredLoginError } from "../types";

const signInWithRedirect = async (
  provider: GoogleAuthProvider | SAMLAuthProvider,
): Promise<UserCredential | null> => {
  try {
    await authSignInWithRedirect(firebaseAuth, provider);

    return await getRedirectResult(firebaseAuth);
  } catch (error) {
    logger.error(`[socialAuth][signInWithRedirect] Error: ${error}`);
  }
  return null;
};

export const signInWithProvider = async (
  provider:
    | GoogleAuthProvider
    | FacebookAuthProvider
    | SAMLAuthProvider
    | OAuthProvider,
  isPopup = false,
  isSSOEventLinkLogin = false,
): Promise<UserCredential | null> => {
  toggleSSOEventLinkSource(isSSOEventLinkLogin);
  if (!isPopup) {
    return signInWithRedirect(provider);
  }

  try {
    return await signInWithPopup(firebaseAuth, provider);
  } catch (error) {
    const firebaseError = error as FirebaseError;
    // Error codes: https://firebase.google.com/docs/reference/js/firebase.auth.Auth#error-codes_15
    switch (firebaseError.code) {
      case "auth/popup-blocked":
      case "auth/cancelled-popup-request":
        logger.info(
          "[socialAuth][signInWithProvider] Popup request cancelled or blocked",
        );
        break;
      case "auth/popup-closed-by-user":
        logger.info("[socialAuth][signInWithProvider] User closed the popup");
        break;
      case "auth/network-request-failed":
        logger.info(
          "[socialAuth][signInWithProvider] Network error. Attempting to signInWithRedirect",
        );
        break;
      case "auth/account-exists-with-different-credential": {
        logger.info(
          "[socialAuth][signInWithProvider] Account error. Existing email attempting to signIn with a different provider",
        );
        if (firebaseError.customData && firebaseError.customData.email) {
          const methods = await fetchSignInMethodsForEmail(
            firebaseAuth,
            firebaseError.customData.email as string,
          );
          throw new PreferredLoginError(
            "auth/account-exists-with-different-credential",
            methods,
          );
        }

        throw firebaseError as Error; // pass the error up
      }
      default:
        logger.error(
          `[socialAuth][signInWithProvider] Error: '${firebaseError}'`,
        );
        break;
    }

    return signInWithRedirect(provider);
  }
};

export const signInWithSAML = async (
  providerName: string,
  isPopup: boolean,
  isSSOEventLinkLogin = false,
): Promise<UserCredential | null> => {
  const SAMLProvider = new SAMLAuthProvider(providerName);
  return signInWithProvider(SAMLProvider, isPopup, isSSOEventLinkLogin);
};
